"use client";

import React, { createContext, useContext, ReactNode, useMemo, useEffect, useCallback } from "react";
import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import {
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  addDays,
} from "date-fns";
import { useCombinedCalendarData } from "../hooks/use-api";
import { useAccountStatus } from "../hooks/use-api";
import { useUniversalAuth } from "./auth/OptimalAuthProvider";
import { useAuth } from "../hooks/useAuth";
import { apiGet } from "../lib/api-utils";
import { buildQueryParams } from "../hooks/api/utils";
import { QUERY_CONFIG } from "../hooks/api/config";
import { normalizeGoogleEvent, normalizeMicrosoftEvent } from "../lib/calendar-utils";
import type { CalendarEvent, CalendarDataParams, GoogleCalendarEvent, MicrosoftCalendarEvent } from "../types/calendar";
import {
  LEGACY_WTC_EMAIL_CALENDAR_SOURCE_ID,
  WTC_EMAIL_CALENDAR_SOURCE_ID,
} from "../hooks/api/calendar/constants";

export type CalendarViewType = "dayGridMonth" | "timeGridWeek" | "timeGridDay" | "listWeek";

// Color palette for calendar sources
const CALENDAR_SOURCE_COLORS = [
  "#3B82F6", // Blue
  "#EF4444", // Red
  "#10B981", // Green
  "#F59E0B", // Yellow
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#06B6D4", // Cyan
  "#84CC16", // Lime
  "#F97316", // Orange
  "#6366F1", // Indigo
  "#14B8A6", // Teal
  "#EF4444", // Rose
];

interface CalendarSource {
  email: string;
  color: string;
  eventCount: number;
}

interface CalendarContextType {
  // Data
  events: CalendarEvent[];
  isLoading: boolean;
  error: any;
  
  // Source management
  calendarSources: CalendarSource[];
  sourceColors: Record<string, string>;
  
  // Date range management
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  weekStart: Date;
  weekEnd: Date;
  
  // View management
  currentView: CalendarViewType;
  
  // Person selection (now supports multiple calendars)
  selectedCalendars: string[];
  setSelectedCalendars: (calendars: string[]) => void;
  toggleCalendar: (email: string) => void;
  // Legacy support - maps to first selected calendar
  selectedPerson: string;
  setSelectedPerson: (person: string) => void;
  setCurrentView: (view: CalendarViewType) => void;
  
  // Contact search for attendee selection
  contacts?: Array<{ id: string; name: string; email: string; company_name?: string }>;
  onSearchContacts?: (searchTerm: string) => Promise<Array<{ id: string; name: string; email: string; company_name?: string }>>;
  isLoadingContacts?: boolean;
  
  // Utility functions
  refetchEvents: () => void;
  invalidateCache: () => void;
  
  // Navigation helpers
  goToPreviousWeek: () => void;
  goToNextWeek: () => void;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  goToToday: () => void;
}

const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

interface CalendarProviderProps {
  children: ReactNode;
  initialDate?: Date;
  initialView?: CalendarViewType;
  /** Contiguous calendar months to load around `currentDate` (min 1). Default 1 = visible month(s) only. */
  dateRangeMonths?: number;
  enabled?: boolean; // Whether to enable calendar data fetching
  // Contact search props for attendee selection
  contacts?: Array<{ id: string; name: string; email: string; company_name?: string }>;
  onSearchContacts?: (searchTerm: string) => Promise<Array<{ id: string; name: string; email: string; company_name?: string }>>;
  isLoadingContacts?: boolean;
}

export function CalendarProvider({ 
  children, 
  initialDate,
  initialView = "dayGridMonth",
  dateRangeMonths = 1,
  enabled = true,
  contacts,
  onSearchContacts,
  isLoadingContacts
}: CalendarProviderProps) {
  // Try to get user from Supabase auth context, but don't fail if not available
  let user = null;
  try {
    const authContext = useUniversalAuth();
    user = authContext.appUser;
  } catch (error) {
    // SupabaseAuthProvider not available, continue without user
    console.warn('SupabaseAuthProvider not available in CalendarProvider, continuing without user context');
  }
  
  const { data: accountStatus } = useAccountStatus();
  // `initialDate = new Date()` used to be a default PARAMETER, so it was read on
  // every render of every page — this provider is mounted in hub's root layout.
  // Under `cacheComponents` an unstable value read during a prerender is a hard
  // build error: the prerendered HTML would bake in the build time and serve it
  // to every visitor. Start from a stable epoch and correct to "now" once on the
  // client. Nothing fetches with the placeholder — every calendar query below is
  // additionally gated on a resolved user + token, neither of which exists
  // during a prerender.
  const [currentDate, setCurrentDate] = React.useState<Date>(
    () => initialDate ?? new Date(0)
  );
  React.useEffect(() => {
    if (initialDate) return;
    setCurrentDate((previous) => (previous.getTime() === 0 ? new Date() : previous));
  }, [initialDate]);
  const [currentView, setCurrentView] = React.useState<CalendarViewType>(initialView);
  // Initialize selectedCalendars as empty - defaults will be set once via useEffect
  const [selectedCalendars, setSelectedCalendars] = React.useState<string[]>([]);
  
  // Track if we've initialized the default selection (only once, on first load)
  const didInitSelectionRef = React.useRef(false);
  
  // Build list of authenticated identity emails ONLY
  // Only include emails from accounts that are actually connected/authenticated
  // This ensures external calendars (shared/delegated) are never treated as identity emails
  const myIdentityEmails = React.useMemo(() => {
    const emails: string[] = [];
    
    // Only add Google primary email if account is actually connected
    if (accountStatus?.google?.primary && accountStatus?.google?.primaryEmail) {
      emails.push(accountStatus.google.primaryEmail);
    }
    
    // Only add Google secondary email if account is actually connected
    if (accountStatus?.google?.secondary && accountStatus?.google?.secondaryEmail) {
      emails.push(accountStatus.google.secondaryEmail);
    }
    
    // Only add Microsoft email if account is actually connected
    if (accountStatus?.microsoft && accountStatus?.microsoftEmail) {
      emails.push(accountStatus.microsoftEmail);
    }
    
    return emails.map(e => e.toLowerCase().trim());
  }, [accountStatus]);

  // Build a set of all emails that belong to the user (hub + authenticated identity emails)
  // These are aliases and should never be fetched as "other calendars"
  // External calendars (shared/delegated) are NOT included here - they will be fetched separately
  const myEmailSet = React.useMemo(() => {
    const set = new Set<string>();
    if (user?.email) {
      set.add(user.email.toLowerCase().trim());
    }
    // Only add authenticated identity emails (already filtered by connection flags)
    myIdentityEmails.forEach(email => set.add(email));
    return set;
  }, [user?.email, myIdentityEmails]);

  const queryClient = useQueryClient();
  
  // Set default selection ONCE on first load (only if user hasn't interacted yet)
  React.useEffect(() => {
    if (didInitSelectionRef.current) return;
    if (!user?.email) return;
    
    // Default to showing user calendar ON FIRST LOAD ONLY
    setSelectedCalendars([user.email]);
    didInitSelectionRef.current = true;
  }, [user?.email]);
  
  // Legacy support: selectedPerson maps to first selected calendar
  const selectedPerson = selectedCalendars[0] || "";
  const setSelectedPerson = useCallback((person: string) => {
    if (person) {
      setSelectedCalendars([person]);
    }
  }, []);

  // Toggle a calendar in the selected calendars list
  const toggleCalendar = useCallback((email: string) => {
    setSelectedCalendars(prev => {
      const normalizedEmail = email.toLowerCase().trim();
      const normalizedPrev = prev.map(e => e.toLowerCase().trim());
      
      if (normalizedPrev.includes(normalizedEmail)) {
        // Remove if already selected
        return prev.filter(e => e.toLowerCase().trim() !== normalizedEmail);
      } else {
        // Add if not selected
        return [...prev, email];
      }
    });
  }, []);

  // Small API window: N contiguous calendar months (default 1) + edge padding for all-day / multi-day spill.
  const rangeMonthStart = startOfMonth(currentDate);
  const rangeMonthEnd = endOfMonth(addMonths(currentDate, Math.max(0, dateRangeMonths - 1)));
  const extendedStartDate = startOfDay(subDays(rangeMonthStart, 7));
  const extendedEndDate = endOfDay(addDays(rangeMonthEnd, 7));
  
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 }); // Monday

  // Calculate date range parameters.
  // Anchored to `currentDate`, not to `new Date()`. Reading the clock here ran on
  // every render of every page (this provider is in hub's root layout), which
  // `cacheComponents` rejects during a prerender. `currentDate` is the reference
  // date every other value in this block already derives from, so it is both the
  // stable choice and the semantically consistent one. `monthsAgo` is only a
  // fallback hint anyway — `startDate`/`endDate` below are passed explicitly and
  // take precedence in the API.
  const monthsAgo = Math.ceil((currentDate.getTime() - extendedStartDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
  const calendarParams: CalendarDataParams = {
    monthsAgo: Math.max(1, monthsAgo),
    // Hub/API routes use an anchored "today ± months" window unless these are set; passing the
    // visible range keeps events in sync when navigating many months ahead or back.
    startDate: startOfDay(extendedStartDate).toISOString(),
    endDate: endOfDay(extendedEndDate).toISOString(),
  };

  // Get auth token for API calls
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  // Check if user's calendar is selected (only check for user.email, not identity emails)
  // Empty selection = show nothing (no default to true)
  const isUserCalendarSelected = React.useMemo(() => {
    if (!user?.email) return false;
    if (selectedCalendars.length === 0) return false; // Empty selection = show nothing
    const selected = new Set(selectedCalendars.map(e => e.toLowerCase().trim()));
    return selected.has(user.email.toLowerCase().trim());
  }, [user?.email, selectedCalendars]);
  

  // Fetch user's own calendar (Google + Microsoft combined) only if selected
  const userCalendarData = useCombinedCalendarData({
    ...calendarParams,
    email: undefined, // User's own calendar — Google/MS use account status; WTC Email uses session user in the hook
    enabled: enabled && !!user?.email && !!token && isUserCalendarSelected
  });

  // Fetch events for each selected calendar (excluding user's own and all identity emails)
  // Identity emails are aliases and should never be fetched as "other calendars"
  const otherCalendarsQueries = useQueries({
    queries: selectedCalendars
      .filter(email => {
        const normalizedEmail = email.toLowerCase().trim();
        // Exclude all emails that belong to the user (hub + identity emails)
        return !myEmailSet.has(normalizedEmail);
      })
      .map(email => {
        const normalizedEmail = email.toLowerCase().trim();
        return {
          queryKey: ["calendar-events", normalizedEmail, calendarParams],
          queryFn: async (): Promise<CalendarEvent[]> => {
            if (!token) {
              throw new Error("No authentication token");
            }

            const queryParams = buildQueryParams({
              email: normalizedEmail,
              monthsAgo: calendarParams.monthsAgo,
              startDate: calendarParams.startDate,
              endDate: calendarParams.endDate,
            });

            const events: CalendarEvent[] = [];

            // Fetch Google calendar events
            try {
              const googleResponse = await apiGet(
                "/unifiedtime/getCalendar/google",
                token,
                queryParams,
              );
              if (Array.isArray(googleResponse)) {
                const normalized = (googleResponse as GoogleCalendarEvent[]).map(e => normalizeGoogleEvent(e, normalizedEmail));
                events.push(...normalized);
              }
            } catch (error: any) {
              // Calendar might not be connected for this email - that's okay
              if (error.response?.status !== 422) {
                console.warn(`Failed to fetch Google calendar for ${normalizedEmail}:`, error);
              }
            }

            // Fetch Microsoft calendar events
            try {
              const microsoftResponse = await apiGet(
                "/unifiedtime/getCalendar/msft",
                token,
                queryParams,
              );
              if (Array.isArray(microsoftResponse)) {
                const normalized = (microsoftResponse as MicrosoftCalendarEvent[]).map(e => normalizeMicrosoftEvent(e, normalizedEmail));
                events.push(...normalized);
              }
            } catch (error: any) {
              // Calendar might not be connected for this email - that's okay
              if (error.response?.status !== 422) {
                console.warn(`Failed to fetch Microsoft calendar for ${normalizedEmail}:`, error);
              }
            }

            return events;
          },
          enabled: enabled && !!normalizedEmail && !!token,
          staleTime: QUERY_CONFIG.calendar.staleTime,
          gcTime: QUERY_CONFIG.calendar.gcTime,
          retry: 1, // Prevent stuck loading from excessive retries
        };
      })
  });

  // Combine all events from all selected calendars
  // Create a stable dependency key from the queries
  const queriesDataKey = React.useMemo(() => {
    return otherCalendarsQueries.map(q => `${q.dataUpdatedAt || 0}-${q.data?.length || 0}`).join('|');
  }, [otherCalendarsQueries]);

  const allEvents = React.useMemo(() => {
    const events: CalendarEvent[] = [];
    
    // Add user's own calendar events if user's email is selected
    // This includes events from all identity emails (Google/Microsoft) since they're aliases
    if (isUserCalendarSelected && userCalendarData.data) {
      events.push(...userCalendarData.data);
    }
    
    // Add events from other selected calendars (non-user calendars)
    // These queries are already filtered to exclude myEmailSet
    otherCalendarsQueries.forEach((query) => {
      if (query.data && Array.isArray(query.data)) {
        events.push(...query.data);
      }
    });
    
    return events;
    // Note: otherCalendarsQueries is accessed from closure but changes are tracked via queriesDataKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCalendarData.data, queriesDataKey, isUserCalendarSelected]);
  
  // Filter events to only show events from selected calendars
  // If user.email is selected, allow events from any identity email (they're aliases)
  // If another calendar is selected, only allow its exact email match
  // Empty selection = show nothing
  const events = React.useMemo(() => {
    if (selectedCalendars.length === 0) return []; // Empty selection = show nothing

    const selectedSet = new Set(
      selectedCalendars.map(e => e.toLowerCase().trim())
    );

    const isUserEmailSelected = user?.email && selectedSet.has(user.email.toLowerCase().trim());

    return allEvents.filter((event) => {
      const src = event?.source?.toLowerCase().trim();
      if (!src) return false;

      const provider = (event as CalendarEvent & { provider?: string }).provider;

      // If user.email is selected, allow events from any identity email
      if (isUserEmailSelected && myEmailSet.has(src)) {
        return true;
      }

      // WTC Email (mail app): source may be a stable id or legacy placeholder, not in myEmailSet
      if (
        isUserEmailSelected &&
        (provider === "professional-email-calendar" ||
          src === WTC_EMAIL_CALENDAR_SOURCE_ID ||
          src === LEGACY_WTC_EMAIL_CALENDAR_SOURCE_ID)
      ) {
        return true;
      }

      // For other calendars, require exact match
      return selectedSet.has(src);
    });
  }, [allEvents, selectedCalendars, user?.email, myEmailSet]);
  
  // Combine loading states from all queries
  const isLoading = userCalendarData.isLoading || otherCalendarsQueries.some(q => q.isLoading);
  
  // Combine errors from all queries
  const error = userCalendarData.error || otherCalendarsQueries.find(q => q.error)?.error || null;
  
  // Refetch function that refetches all queries
  const refetch = React.useCallback(async () => {
    // Invalidate user calendar queries (Google, Microsoft, WTC Email)
    await queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
    await queryClient.invalidateQueries({ queryKey: ["microsoft-calendar"] });
    await queryClient.invalidateQueries({ queryKey: ["calendar-events", "professional-email"] });
    
    // Refetch other calendars
    await Promise.all(
      otherCalendarsQueries.map(q => q.refetch())
    );
  }, [queryClient, otherCalendarsQueries]);

  // Debug logging removed to prevent infinite loops
  // Note: React Query will automatically refetch when query keys change (selectedCalendars)
  // No need to manually trigger refetch here


  // Extract unique calendar sources and assign colors
  const { calendarSources, sourceColors } = useMemo(() => {
    // Get unique sources from events
    const uniqueSources = Array.from(
      new Set(events.map((event) => event.source).filter(Boolean))
    );

    // Create source objects with colors and event counts
    const sources: CalendarSource[] = uniqueSources.map((source, index) => {
      const eventCount = events.filter((event) => event.source === source).length;
      const color = CALENDAR_SOURCE_COLORS[index % CALENDAR_SOURCE_COLORS.length];

      return {
        email: source,
        color,
        eventCount,
      };
    });

    // Create color mapping for easy lookup
    const colors: Record<string, string> = {};
    sources.forEach((source) => {
      colors[source.email] = source.color;
    });

    const wtc = userCalendarData.wtcEmailCalendar;
    const wtcId = WTC_EMAIL_CALENDAR_SOURCE_ID;
    const hasWtcRow = sources.some(
      (s) => s.email === wtcId || s.email === LEGACY_WTC_EMAIL_CALENDAR_SOURCE_ID
    );
    if (
      wtc?.queryEnabled &&
      wtc.isFetched &&
      !wtc.isLoading &&
      !hasWtcRow
    ) {
      const wtcColor = "#7c3aed";
      sources.push({
        email: wtcId,
        color: wtcColor,
        eventCount: wtc.isError ? 0 : wtc.eventCount,
      });
      colors[wtcId] = wtcColor;
    }

    return {
      calendarSources: sources,
      sourceColors: colors,
    };
  }, [events, userCalendarData.wtcEmailCalendar]);

  // Navigation helpers - memoized to prevent unnecessary re-renders
  const goToPreviousWeek = useCallback(() => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);

      return newDate;
    });
  }, []);

  const goToNextWeek = useCallback(() => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);

      return newDate;
    });
  }, []);

  const goToPreviousMonth = useCallback(() => {
    setCurrentDate(prev => subMonths(prev, 1));
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentDate(prev => addMonths(prev, 1));
  }, []);

  const goToPreviousDay = useCallback(() => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);

      return newDate;
    });
  }, []);

  const goToNextDay = useCallback(() => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);

      return newDate;
    });
  }, []);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Cache invalidation - memoized to prevent unnecessary re-renders
  const invalidateCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["combined-calendar"] });
    queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
    queryClient.invalidateQueries({ queryKey: ["microsoft-calendar"] });
    queryClient.invalidateQueries({ queryKey: ["calendar-events", "professional-email"] });
  }, [queryClient]);

  const refetchEvents = useCallback(() => {
    refetch();
  }, [refetch]);

  const contextValue: CalendarContextType = useMemo(() => ({
    // Data
    events,
    isLoading,
    error,
    
    // Source management
    calendarSources,
    sourceColors,
    
    // Date range management
    currentDate,
    setCurrentDate,
    weekStart,
    weekEnd,
    
    // View management
    currentView,
    setCurrentView,
    
    // Person selection (multi-select)
    selectedCalendars,
    setSelectedCalendars,
    toggleCalendar,
    // Legacy support
    selectedPerson,
    setSelectedPerson,
    
    // Contact search for attendee selection
    contacts,
    onSearchContacts,
    isLoadingContacts,
    
    // Utility functions
    refetchEvents,
    invalidateCache,
    
    // Navigation helpers
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousDay,
    goToNextDay,
    goToToday,
  }), [
    events,
    isLoading,
    error,
    calendarSources,
    sourceColors,
    currentDate,
    weekStart,
    weekEnd,
    currentView,
    selectedCalendars,
    toggleCalendar,
    selectedPerson,
    setSelectedPerson,
    contacts,
    onSearchContacts,
    isLoadingContacts,
    refetchEvents,
    invalidateCache,
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousDay,
    goToNextDay,
    goToToday,
  ]);

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
}

export function useCalendarContext(): CalendarContextType {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error("useCalendarContext must be used within a CalendarProvider");
  }

  return context;
}


// Convenience hook for getting events in a specific date range
export function useCalendarEvents(startDate?: Date, endDate?: Date): CalendarEvent[] {
  const { events } = useCalendarContext();
  
  if (!startDate || !endDate) {
    return events;
  }

  return events.filter(event => {
    const eventStart = new Date(event.start);

    return eventStart >= startDate && eventStart <= endDate;
  });
}

// Convenience hook for getting a specific event by ID
export function useCalendarEvent(eventId: string): CalendarEvent | undefined {
  const { events } = useCalendarContext();

  return events.find(event => event.id === eventId);
}

// Convenience hook for getting calendar sources
export function useCalendarSources(): CalendarSource[] {
  const { calendarSources } = useCalendarContext();
  
return calendarSources;
}

// Convenience hook for getting source colors
export function useSourceColors(): Record<string, string> {
  const { sourceColors } = useCalendarContext();
  
return sourceColors;
}

// Convenience hook for getting color for a specific source
export function useSourceColor(sourceEmail: string): string | undefined {
  const { sourceColors } = useCalendarContext();
  
return sourceColors[sourceEmail];
}